import { apiGet, apiPost, apiPut, apiDelete } from './api';

interface AdminEventDetail {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  type: string;
  status: string;
  location: string | null;
  venue: string | null;
  startDate: string;
  endDate: string | null;
  capacity: number | null;
  image: string | null;
  website: string | null;
  ticketUrl: string | null;
  price: string | null;
  isPublic: boolean;
  creatorId: number;
  createdAt: string;
  updatedAt: string;
  // Post-moderation: events go live immediately, admin reviews afterwards
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  creator: {
    id: number;
    username: string;
    email: string;
    profilePhoto: string | null;
  };
  statistics: {
    totalAttendees: number;
    interestedUsers: number;
    goingUsers: number;
    notGoingUsers: number;
    associatedGroups: number;
  };
  attendees: Array<{
    id: number;
    user: {
      id: number;
      username: string;
      email: string;
      profilePhoto: string | null;
      createdAt: string;
    };
    status: 'INTERESTED' | 'GOING' | 'NOT_GOING';
    joinedAt: string;
    updatedAt: string;
  }>;
  groups: Array<{
    id: number;
    name: string;
    description: string | null;
    memberCount: number;
    owner: {
      id: number;
      username: string;
    };
    createdAt: string;
  }>;
  organizers: Array<{
    id: number;
    role: string;
    addedAt: string;
    user: {
      id: number;
      username: string;
      email: string;
      profilePhoto: string | null;
    };
  }>;
  tags: Array<{
    id: number;
    name: string;
    color: string | null;
  }>;
}

interface BulkOperationResult {
  approvedCount?: number;
  rejectedCount?: number;
  events: Array<{
    id: number;
    title: string;
    creator: {
      id: number;
      username: string;
      email: string;
    };
  }>;
}

class AdminEventService {
  /**
   * Get detailed event information for admin
   */
  async getEventDetail(eventId: number): Promise<AdminEventDetail> {
    return apiGet<AdminEventDetail>(`/events/admin/${eventId}`);
  }

  /**
   * Bulk approve events (admin only)
   */
  async bulkApproveEvents(eventIds: number[]): Promise<BulkOperationResult> {
    return apiPost<BulkOperationResult>('/events/admin/bulk-approve', { eventIds });
  }

  /**
   * Bulk reject events (admin only)
   */
  async bulkRejectEvents(eventIds: number[], reason?: string): Promise<BulkOperationResult> {
    return apiPost<BulkOperationResult>('/events/admin/bulk-reject', { eventIds, reason });
  }

  /**
   * Update event (admin only)
   */
  async updateEvent(eventId: number, updateData: {
    title?: string;
    description?: string;
    slug?: string;
    location?: string;
    startDate?: string | null;
    endDate?: string | null;
    image?: string | null;
  }): Promise<AdminEventDetail> {
    return apiPut<AdminEventDetail>(`/events/admin/${eventId}`, updateData);
  }

  /**
   * Update event with file upload (admin only)
   */
  async updateEventWithFile(eventId: number, formData: FormData): Promise<AdminEventDetail> {
    return apiPut<AdminEventDetail>(`/events/admin/${eventId}`, formData);
  }

  /**
   * Permanently delete an event and all its data (groups, messages, attendees).
   * Hard delete — cannot be undone. Admin only.
   */
  async deleteEventById(eventId: number): Promise<void> {
    return apiDelete(`/events/admin/${eventId}`);
  }
}

export const adminEventService = new AdminEventService();
export type { AdminEventDetail, BulkOperationResult };